import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useMemo,
} from "react";
import { Box, CircularProgress, Divider, Typography } from "@mui/material";
import { useErrorDialog } from "../contexts/ErrorDialogContext";
import { useSuccessDialog } from "../contexts/SuccessDialogContext";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { v4 as uuidv4 } from "uuid";

import { DETAIL_PL } from "../utils/data/controlData";
import AccessDenied from "../components/Common/AccessDenied";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { Header, Details } from "../components/DetailedP&L";
import { AuthContext } from "../contexts/AuthContext";
import ComponentLoading from "../components/App/ComponentLoading";
import Notes from "../components/Schedules/utils/Notes";
import {
  detailsPLSchedules_tagging,
  investmentDetailsPLSchedules_tagging,
} from "../utils/static/detailsPL";
import PlSummary from "../components/DetailedP&L/P&LSummary";
import { useAccessControl } from "../utils/hooks/useAccessControl";
import {
  AdjustmentManagementDialog,
  hydrateLinkedRows,
  useScheduleTagging,
} from "../components/Common/TaxAdjustment";

const sections = ["Revenue", "Other Income", "Cost of Sales", "Expenses"];

const mapClassifiedRowToPlRow = (rowData) => {
  const history = Array.isArray(rowData.history) ? rowData.history : [];
  const hasMerge = history.length > 1;
  return {
    row_id: uuidv4(),
    name: rowData.accountName || rowData.name || "",
    amount: Number(rowData.amount) || 0,
    historyAmount: hasMerge
      ? history.map((item) => Number(item.amount) || 0)
      : [Number(rowData.amount) || 0],
    taxAdjustment: rowData.taxAdjustment ?? "",
    history: hasMerge
      ? history.map((item) => ({
          name: item.name || "",
          amount: Number(item.amount) || 0,
          accountCode: item.accountCode || "",
          taxAdjustment: item.taxAdjustment ?? "",
        }))
      : [],
    accountCode: rowData.accountCode || "",
    reference: rowData.reference || "",
  };
};

const DetailedPL = forwardRef(
  (
    {
      company,
      openScheduleWithDelay,
    },
    ref
  ) => {
    const { unTagSchedule } = useScheduleTagging();
    const handleSuccess = useSuccessDialog();
    const handleError = useErrorDialog();

    const { canView: canViewDetailPL, canEdit: canEditDetailPL } =
      useAccessControl(DETAIL_PL);

    const [revenue, setRevenue] = useState([]);
    const [otherIncome, setOtherIncome] = useState([]);
    const [costOfSales, setCostOfSales] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [comments, setComments] = useState("");
    const [reviewNote, setReviewNote] = useState("");
    const [currency, setCurrency] = useState("SGD");
    const [currencyRate, setCurrencyRate] = useState(1);
    const [currencyView, setCurrencyView] = useState("SGD");
    const [detailsId, setDetailsId] = useState("");
    const [flipSignMode, setFlipSignMode] = useState({
      Revenue: "positive",
      "Other Income": "positive",
      "Cost of Sales": "negative",
      Expenses: "negative",
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [sectionTotals, setSectionTotals] = useState({
      Revenue: 0,
      "Other Income": 0,
      "Cost of Sales": 0,
      Expenses: 0,
    });
    const [netProfitBeforeTax, setNetProfitBeforeTax] = useState(0);
    const [shouldRecordChanges, setShouldRecordChanges] = useState(false);
    const [adjustmentManagementOpen, setAdjustmentManagementOpen] =
      useState(false);

    const {
      updateDetailsPL,
      getDetailsPLData,
    } = useContext(AuthContext);

    const [isDirty, setIsDirty] = useState(false);
    const [shouldConfirmExit, setShouldConfirmExit] = useState(false);
    useImperativeHandle(ref, () => ({
      isDirty: () => isDirty,
      shouldConfirmExit: () => shouldConfirmExit,
      getUnsavedData: () => {
        return {
          tc_id: company.tc_id,
          financialYearEnd: company.financialYearEnd,
          financialYearStart: company.financialYearStart,
          yearOfAssessment: company.yearOfAssessment,
          revenue: revenue,
          costOfSale: costOfSales,
          Expenses: expenses,
          otherIncome: otherIncome,
          flipSignMode: flipSignMode,
          comments: comments,
          reviewNote: reviewNote,
          netProfitBeforeTax,
          doc: JSON.stringify({
            company: company,
            revenue: revenue,
            otherIncome: otherIncome,
            costOfSales: costOfSales,
            expenses: expenses,
            sectionTotals: sectionTotals,
            currencyView: currencyView,
            notes: comments,
            reviewNotes: reviewNote,
            flipSignMode: flipSignMode,
          }),
        };
      },
      clearDirtyFlag: () => {
        setIsDirty(false);
        setShouldConfirmExit(false);
      },
    }));

    const recordShouldConfirmExit = () => setShouldConfirmExit(true);
    const recordChangeHappened = () => setIsDirty(true);
    const recordSaved = () => setIsDirty(false);

    // Update row functions
    const updateRevenueRow = useCallback((rowIndex, newRow) => {
      setRevenue((prev) =>
        prev.map((row, idx) => (idx === rowIndex ? newRow : row))
      );
    }, []);
    const updateOtherIncomeRow = useCallback((rowIndex, newRow) => {
      setOtherIncome((prev) =>
        prev.map((row, idx) => (idx === rowIndex ? newRow : row))
      );
    }, []);
    const updateCostOfSalesRow = useCallback((rowIndex, newRow) => {
      setCostOfSales((prev) =>
        prev.map((row, idx) => (idx === rowIndex ? newRow : row))
      );
    }, []);
    const updateExpensesRow = useCallback((rowIndex, newRow) => {
      setExpenses((prev) =>
        prev.map((row, idx) => (idx === rowIndex ? newRow : row))
      );
    }, []);

    const updateSectionRow = (section, rowIndex, newRow) => {
      switch (section) {
        case "Revenue":
          updateRevenueRow(rowIndex, newRow);
          break;
        case "Other Income":
          updateOtherIncomeRow(rowIndex, newRow);
          break;
        case "Cost of Sales":
          updateCostOfSalesRow(rowIndex, newRow);
          break;
        case "Expenses":
          updateExpensesRow(rowIndex, newRow);
          break;
        default:
          break;
      }

      setShouldRecordChanges(true);
    };

    const updateSectionRows = useCallback((section, newRows) => {
      switch (section) {
        case "Revenue":
          setRevenue(newRows);
          break;
        case "Other Income":
          setOtherIncome(newRows);
          break;
        case "Cost of Sales":
          setCostOfSales(newRows);
          break;
        case "Expenses":
          setExpenses(newRows);
          break;
        default:
          break;
      }
      setShouldRecordChanges(true);
    }, []);

    const updateSectionSign = useCallback((section) => {
      setFlipSignMode((prev) => ({
        ...prev,
        [section]: prev[section] === "positive" ? "negative" : "positive",
      }));
    }, []);

    // Helper to get the correct array for a section
    const getSectionData = useCallback(
      (section) => {
        switch (section) {
          case "Revenue":
            return revenue;
          case "Other Income":
            return otherIncome;
          case "Cost of Sales":
            return costOfSales;
          case "Expenses":
            return expenses;
          default:
            return [];
        }
      },
      [revenue, otherIncome, costOfSales, expenses]
    );

    const rowsBySection = useMemo(
      () => ({
        Revenue: revenue,
        "Other Income": otherIncome,
        "Cost of Sales": costOfSales,
        Expenses: expenses,
      }),
      [revenue, otherIncome, costOfSales, expenses]
    );

    const handleClassifyApply = useCallback(
      async (mode, rowsBySection) => {
        try {
          if (mode === "replace") {
            for (const section of sections) {
              const currentRows = getSectionData(section);
              const taggedRows = currentRows.filter((row) => row.schedule);
              if (taggedRows.length && unTagSchedule) {
                await Promise.all(
                  taggedRows.map((row) =>
                    unTagSchedule(
                      "p_l",
                      row.row_id,
                      row.schedule.scheduleType === "FAAdditions"
                        ? "ca_schedule"
                        : "pl_schedule",
                      false
                    )
                  )
                );
              }
              const nextRows = (rowsBySection[section] || []).map(
                mapClassifiedRowToPlRow
              );
              updateSectionRows(section, nextRows);
            }
          } else {
            sections.forEach((section) => {
              const incoming = (rowsBySection[section] || []).map(
                mapClassifiedRowToPlRow
              );
              if (!incoming.length) return;
              updateSectionRows(section, [
                ...getSectionData(section),
                ...incoming,
              ]);
            });
          }

          handleSuccess(
            mode === "replace"
              ? "Classified accounts replaced the current detailed P&L."
              : "Classified accounts were added to the current detailed P&L."
          );
        } catch (error) {
          handleError(
            error?.message
              ? error
              : {
                  message:
                    "The classified accounts could not be applied. Please try again.",
                }
          );
          throw error;
        }
      },
      [getSectionData, handleError, handleSuccess, unTagSchedule, updateSectionRows]
    );
    const handleExcelAddRows = useCallback(
      (section, rows, investmentCompanyType) => {
        const newRows = rows.map((rowData) => ({
          row_id: uuidv4(),
          name: rowData.accountName || "",
          amount: rowData.amount || 0,
          historyAmount:
            rowData.history && rowData.history.length > 1
              ? rowData.history.map((h) => h.amount)
              : [rowData.amount],
          taxAdjustment: rowData.taxAdjustment,
          history:
            rowData.history && rowData.history.length > 1
              ? rowData.history
              : [],
          accountCode: rowData.accountCode || "",
          reference: "",
        }));

        const currentRows = getSectionData(section);
        updateSectionRows(section, [...currentRows, ...newRows]);
      },
      [getSectionData, updateSectionRows]
    );

    // Calculate and store totals when data or flipSignMode changes
    useEffect(() => {
      const newTotals = {};
      let totalNetProfit = 0;

      sections.forEach((section) => {
        try {
          if (!(section in flipSignMode)) {
            throw new Error(
              `Invalid section: ${section} is not defined in flipSignMode.`
            );
          }
          const isNegative = flipSignMode[section] === "negative";
          const sectionData = getSectionData(section);
          const total = sectionData.reduce((sum, row) => {
            const amount = parseFloat(row.amount);
            const validAmount = !isNaN(amount) ? amount : 0;
            return isNegative ? sum - validAmount : sum + validAmount;
          }, 0);
          newTotals[section] = total;

          // Add to net profit calculation based on flip sign
          totalNetProfit += total;
        } catch (error) {
          if (!error.message)
            error.message =
              "An unexpected error occurs during calculation. Please try again later.";
          handleError(error);
          newTotals[section] = 0;
        }
      });

      setSectionTotals(newTotals);
      setNetProfitBeforeTax(totalNetProfit);
    }, [revenue, otherIncome, costOfSales, expenses, flipSignMode]);

    // Record changes when shouldRecordChanges is true
    useEffect(() => {
      if (shouldRecordChanges) {
        recordChangeHappened();
        setShouldRecordChanges(false);
      }
    }, [shouldRecordChanges, recordChangeHappened]);

    useEffect(() => {
      const handleBeforeUnload = (e) => {
        if (isDirty) {
          e.preventDefault();
          e.returnValue = "";
        }
      };
      window.addEventListener("beforeunload", handleBeforeUnload);
      return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }, [isDirty]);

    useEffect(() => {
      const fetchDetailsPL = async () => {
        setLoading(true);
        try {
          const detailsPL = await getDetailsPLData(company.tc_id);

          setCurrency(detailsPL.currency || "SGD");
          setCurrencyRate(detailsPL.currencyRate || 1);
          setCurrencyView(detailsPL.currency || "SGD");
          setDetailsId(detailsPL.pl_id || "");

          setRevenue(hydrateLinkedRows(detailsPL.Revenue));
          setOtherIncome(hydrateLinkedRows(detailsPL.OtherIncome));
          setCostOfSales(hydrateLinkedRows(detailsPL.CostOfSales));
          setExpenses(hydrateLinkedRows(detailsPL.Expenses));
          setComments(detailsPL.comments || "");
          setReviewNote(detailsPL.reviewNote || "");

          setFlipSignMode(
            detailsPL.flipSignMode || {
              Revenue: "positive",
              "Other Income": "positive",
              "Cost of Sales": "negative",
              Expenses: "negative",
            }
          );

          recordSaved();
        } catch (error) {
          handleError(error);
        } finally {
          setLoading(false);
        }
      };

      fetchDetailsPL();
    }, [company.uen, company.id]);

    const handleCommentsChange = (e) => {
      setComments(e.target.value);
      setShouldRecordChanges(true);
    };

    const handleReviewNoteChange = (e) => {
      setReviewNote(e.target.value);
      setShouldRecordChanges(true);
    };

    const onViewSchedulePage = async (section, index, taxAdjustment) => {
      const selectedRow = getSectionData(section)[index];

      if (!selectedRow.taxAdjustment || !selectedRow.schedule) {
        handleError(
          "There got an issue to view the schedule. Please try again."
        );
        return;
      }

      setShouldConfirmExit(false);

      openScheduleWithDelay(
        selectedRow.schedule,
        company.companyType === "investment"
          ? investmentDetailsPLSchedules_tagging[taxAdjustment]
          : detailsPLSchedules_tagging[taxAdjustment],
        false
      );
    };

    const handleDoneTagging = async (updatedRow, reference) => {
      const updatedRows = getSectionData(reference.section).map((row) =>
        row.row_id === updatedRow.row_id ? updatedRow : row
      );
      try {
        updateSectionRows(reference.section, updatedRows);

        recordSaved();
        recordShouldConfirmExit();

        openScheduleWithDelay(
          updatedRow.schedule || reference.schedule,
          reference.scheduleType
        );
      } catch (error) {
        throw error;
      } finally {
        await handleSave("Tag applied to schedule successfully.", true, {
          section: reference.section,
          updatedRows,
        });
      }
    };

    const handleSave = async (
      successMessage = "Successfully saved the Details P&L.",
      ignoreLoading = false,
      latestSectionSet = {}
    ) => {
      let currentRevenue = revenue;
      let currentOtherIncome = otherIncome;
      let currentCostOfSales = costOfSales;
      let currentExpenses = expenses;

      if (latestSectionSet.sections) {
        currentRevenue = latestSectionSet.sections.Revenue ?? currentRevenue;
        currentOtherIncome =
          latestSectionSet.sections["Other Income"] ?? currentOtherIncome;
        currentCostOfSales =
          latestSectionSet.sections["Cost of Sales"] ?? currentCostOfSales;
        currentExpenses = latestSectionSet.sections.Expenses ?? currentExpenses;
      } else if (latestSectionSet.section && latestSectionSet.updatedRows) {
        switch (latestSectionSet.section) {
          case "Revenue":
            currentRevenue = latestSectionSet.updatedRows;
            break;
          case "Other Income":
            currentOtherIncome = latestSectionSet.updatedRows;
            break;
          case "Cost of Sales":
            currentCostOfSales = latestSectionSet.updatedRows;
            break;
          case "Expenses":
            currentExpenses = latestSectionSet.updatedRows;
            break;
          default:
            break;
        }
      }

      const detailsPLData = {
        tc_id: company.tc_id,
        financialYearEnd: company.financialYearEnd,
        financialYearStart: company.financialYearStart,
        yearOfAssessment: company.yearOfAssessment,
        revenue: currentRevenue,
        costOfSale: currentCostOfSales,
        Expenses: currentExpenses,
        otherIncome: currentOtherIncome,
        flipSignMode: flipSignMode,
        comments: comments,
        reviewNote: reviewNote,
        netProfitBeforeTax,
        doc: JSON.stringify({
          company: company,
          revenue: currentRevenue,
          otherIncome: currentOtherIncome,
          costOfSales: currentCostOfSales,
          expenses: currentExpenses,
          sectionTotals: sectionTotals,
          currencyView: currencyView,
          notes: comments,
          reviewNotes: reviewNote,
          flipSignMode: flipSignMode,
        }),
      };
      if (!ignoreLoading) setSaving(true);
      try {
        const savedDetailsPL = await updateDetailsPL(detailsPLData);
        if (savedDetailsPL?.pl_id) {
          setDetailsId(savedDetailsPL.pl_id);
        }

        handleSuccess(successMessage || "Successfully saved the Details P&L.");
        recordSaved();
      } catch (error) {
        handleError(error);
      } finally {
        if (!ignoreLoading) setSaving(false);
      }
    };

    const handleApplyManagedAdjustments = async (
      nextRowsBySection,
      appliedCount
    ) => {
      setRevenue(nextRowsBySection.Revenue || []);
      setOtherIncome(nextRowsBySection["Other Income"] || []);
      setCostOfSales(nextRowsBySection["Cost of Sales"] || []);
      setExpenses(nextRowsBySection.Expenses || []);
      setShouldConfirmExit(true);

      await handleSave(
        `Applied adjustments to ${appliedCount} row(s).`,
        true,
        { sections: nextRowsBySection }
      );
    };

    // If user doesn't have view access, show access denied
    if (!canViewDetailPL) {
      return (
        <AccessDenied
          title='Access Denied'
          message="You don't have permission to view the detail P&L. Please contact your administrator if you believe this is an error."
          icon={<TrendingUpIcon sx={{ fontSize: 48, color: "grey.400" }} />}
        />
      );
    }

    return (
      <DndProvider backend={HTML5Backend}>
        {loading ? (
          <ComponentLoading />
        ) : (
          <Box sx={{ padding: 2, position: "relative" }}>
            {saving && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: "rgba(255, 255, 255, 0.6)",
                  backdropFilter: "blur(1px)",
                  cursor: "wait",
                }}
              >
                <CircularProgress size={20} />
              </Box>
            )}

            <Header
              company={company}
              revenue={revenue}
              otherIncome={otherIncome}
              costOfSales={costOfSales}
              expenses={expenses}
              sectionTotals={sectionTotals}
              flipSignMode={flipSignMode}
              comments={comments}
              reviewNote={reviewNote}
              currency={currency}
              handleSave={handleSave}
              saving={saving}
              canEditDetailPL={canEditDetailPL}
              onFlipSignModeChange={(newFlipSignMode) => {
                setFlipSignMode(newFlipSignMode);
                setShouldRecordChanges(true);
              }}
              onExcelAddRows={handleExcelAddRows}
              onManageAdjustments={() => setAdjustmentManagementOpen(true)}
              onClassifyApply={handleClassifyApply}
            />

            <br />

            <Details
              company={company}
              detailsId={detailsId}
              tcId={company.tc_id}
              revenue={revenue}
              otherIncome={otherIncome}
              costOfSales={costOfSales}
              expenses={expenses}
              getSectionData={getSectionData}
              updateRevenueRow={updateRevenueRow}
              updateOtherIncomeRow={updateOtherIncomeRow}
              updateCostOfSalesRow={updateCostOfSalesRow}
              updateExpensesRow={updateExpensesRow}
              updateSectionRow={updateSectionRow}
              updateSectionRows={updateSectionRows}
              updateSectionSign={updateSectionSign}
              handleViewSchedulePage={onViewSchedulePage}
              onDoneTagging={handleDoneTagging}
              sections={sections}
              sectionTotals={sectionTotals}
              flipSignMode={flipSignMode}
              canEditDetailPL={canEditDetailPL}
              handleSave={handleSave}
            />

            <PlSummary
              revenueTotal={sectionTotals["Revenue"]}
              costOfSalesTotal={sectionTotals["Cost of Sales"]}
              otherIncomeTotal={sectionTotals["Other Income"]}
              expensesTotal={sectionTotals["Expenses"]}
              netProfitBeforeTax={netProfitBeforeTax}
            />

            <Divider sx={{ marginY: 4 }} />

            <Notes
              label='Notes/comments'
              value={comments}
              onChange={handleCommentsChange}
              enableEdit={canEditDetailPL}
            />
            <br />
            <Notes
              label='Internal Review Notes'
              value={reviewNote}
              onChange={handleReviewNoteChange}
              enableEdit={canEditDetailPL}
            />

            <AdjustmentManagementDialog
              open={adjustmentManagementOpen}
              onClose={() => setAdjustmentManagementOpen(false)}
              companyType={company.companyType}
              tcId={company.tc_id}
              detailsId={detailsId}
              rowsBySection={rowsBySection}
              onApply={handleApplyManagedAdjustments}
              handleError={handleError}
            />
          </Box>
        )}
      </DndProvider>
    );
  }
);

export default DetailedPL;
